import { useState, useEffect, useCallback, Component } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { 
  ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, 
  FileText, Loader2, RotateCw 
} from "lucide-react";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global handler to suppress AbortError from react-pdf (occurs when closing modal during load)
if (typeof window !== 'undefined') {
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Ignore AbortError and BodyStreamBuffer errors from PDF loading
    if (message?.includes('aborted') || message?.includes('BodyStreamBuffer') || 
        error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      return true; // Prevent error from being logged
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Also handle unhandled promise rejections
  window.addEventListener('unhandledrejection', function(event) {
    if (event.reason?.name === 'AbortError' || 
        event.reason?.message?.includes('aborted') ||
        event.reason?.message?.includes('BodyStreamBuffer')) {
      event.preventDefault();
    }
  });
}

// Error Boundary to catch react-pdf errors
class PDFErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Don't set error state for abort errors - they're expected when closing modal
    if (error?.message?.includes('aborted') || error?.message?.includes('BodyStreamBuffer') || 
        error?.name === 'AbortError') {
      return null; // Don't update state
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Ignore abort errors - they're expected when closing modal
    if (error?.message?.includes('aborted') || error?.message?.includes('BodyStreamBuffer') ||
        error?.name === 'AbortError') {
      return;
    }
    console.error("PDF Error:", error);
  }

  componentDidUpdate(prevProps) {
    // Reset error state when invoice changes
    if (prevProps.invoiceNumero !== this.props.invoiceNumero) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <FileText className="w-12 h-12 text-gray-400 mb-2" />
          <p className="text-muted-foreground">Erreur de chargement</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => this.setState({ hasError: false })}
            className="mt-2"
          >
            Réessayer
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export const PDFViewerModal = ({ open, onOpenChange, invoiceNumero, invoiceId, invoiceClient, title, pdfEndpoint = "invoices" }) => {
  const { token } = useAuth();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [key, setKey] = useState(0); // Key to force re-render on error

  useEffect(() => {
    if (open && (invoiceNumero || invoiceId)) {
      setLoading(true);
      setError(null);
      setPageNumber(1);
      setScale(1.0);
      setKey(k => k + 1); // Force new Document instance
      
      // Use ID-based endpoint if invoiceId is provided (preferred for supplier invoices with special chars)
      if (invoiceId && pdfEndpoint === "supplier-invoices") {
        // API already includes /api prefix, so just append the route
        setPdfUrl(`${API}/${pdfEndpoint}/pdf/by-id/${invoiceId}?token=${token}`);
      } else {
        // Fallback to numero-based endpoint
        setPdfUrl(`${API}/${pdfEndpoint}/${encodeURIComponent(invoiceNumero)}/pdf?token=${token}`);
      }
    } else {
      // Clear URL when modal closes to cancel any pending requests
      setPdfUrl(null);
    }
  }, [open, invoiceNumero, invoiceId, pdfEndpoint]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    // Ignore abort errors - they're expected when closing modal quickly
    if (error?.message?.includes('aborted') || error?.message?.includes('BodyStreamBuffer') ||
        error?.name === 'AbortError') {
      return;
    }
    console.error("PDF load error:", error);
    setError("Impossible de charger le PDF");
    setLoading(false);
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${invoiceNumero}.pdf`;
    
    // Add auth header via fetch
    fetch(pdfUrl, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.click();
        window.URL.revokeObjectURL(url);
      });
  };

  const goToPrevPage = () => setPageNumber(p => Math.max(1, p - 1));
  const goToNextPage = () => setPageNumber(p => Math.min(numPages || 1, p + 1));
  const zoomIn = () => setScale(s => Math.min(2, s + 0.2));
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.2));
  const resetZoom = () => setScale(1.0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] sm:h-[90vh] max-h-[100dvh] bg-white p-0 flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 pr-12 sm:pr-14 border-b gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">{invoiceNumero}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{invoiceClient}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-between sm:justify-end">
            {/* Zoom Controls - simplified on mobile */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={zoomOut}
                disabled={scale <= 0.5}
                className="h-8 w-8 p-0"
                title="Zoom arrière"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs sm:text-sm w-10 sm:w-14 text-center">{Math.round(scale * 100)}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={zoomIn}
                disabled={scale >= 2}
                className="h-8 w-8 p-0"
                title="Zoom avant"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetZoom}
                className="h-8 w-8 p-0 hidden sm:flex"
                title="Réinitialiser"
              >
                <RotateCw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="gap-1 sm:gap-2"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Télécharger</span>
            </Button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-start justify-center p-2 sm:p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
              <p className="text-muted-foreground text-sm">Chargement du PDF...</p>
            </div>
          )}
          
          {error && (
            <div className="flex flex-col items-center justify-center h-full">
              <FileText className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}
          
          {pdfUrl && open && (
            <PDFErrorBoundary invoiceNumero={invoiceNumero}>
              <Document
                key={key}
                file={{
                  url: pdfUrl,
                  httpHeaders: { Authorization: `Bearer ${token}` }
                }}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={null}
                className="shadow-lg max-w-full"
              >
                <Page 
                  pageNumber={pageNumber} 
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="bg-white"
                  width={typeof window !== 'undefined' && window.innerWidth < 640 ? window.innerWidth - 32 : undefined}
                />
              </Document>
            </PDFErrorBoundary>
          )}
        </div>

        {/* Footer - Pagination */}
        {numPages && numPages > 0 && (
          <div className="flex items-center justify-center gap-3 sm:gap-4 p-2 sm:p-3 border-t bg-white">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm">
              Page <span className="font-medium">{pageNumber}</span> / <span className="font-medium">{numPages}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFViewerModal;
